import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Box from "@mui/material/Box";
import { useDispatch } from "react-redux";
import { setSelectedWorkspace } from "../../redux/workspace/actions";

/* ============================================================================
 * TVWorkspaceSwitcher
 *
 * A floating, inset, rounded glass panel for picking a workspace on the TV —
 * styled like a TV-OS / "Who's watching" switcher rather than a full-screen
 * carousel. It slides in beside the control rail and floats OVER the board;
 * the board dims behind a light scrim.
 *
 * Remote / D-pad first:
 *   - ↑ / ↓ move the highlight between team cards
 *   - Enter selects (commits the workspace)
 *   - Esc / Backspace closes
 *   - Mouse: click a card to select it, click the scrim to close
 *
 * On select it dispatches `setSelectedWorkspace` only — no navigation — so the
 * TV stays on its route and just re-renders with the new workspace's data.
 * Sub-workspaces are listed right after their parent with a "PARENT ›" prefix.
 * ============================================================================ */

const FONT = `'Space Grotesk', 'DM Sans', 'Inter', system-ui, -apple-system, sans-serif`;
const MONO_FONT = `'Space Grotesk', 'Geist Mono', ui-monospace, monospace`;


/** Top-level workspaces first, each followed by its sub-workspaces. */
function flattenWorkspaces(all = []) {
  const tops = all.filter((w) => !w.parentId);
  const out = [];
  tops.forEach((parent) => {
    out.push({ ...parent, _parentName: null, _isSub: false });
    all.filter((w) => w.parentId === parent._id).forEach((sub) => {
      out.push({ ...sub, _parentName: parent.name, _isSub: true });
    });
  });
  return out;
}

function WorkspaceCard({ workspace, accent, isCurrent, isFocused, dark, onClick, cardRef }) {
  const surface = dark ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.72)";
  const surfaceHover = dark ? "rgba(255,255,255,0.09)" : "#FFFFFF";
  const text = dark ? "#F4F7FC" : "#0B1320";
  const faint = dark ? "rgba(244,247,252,0.4)" : "rgba(11,19,32,0.42)";

  return (
    <Box
      ref={cardRef}
      onClick={onClick}
      role="button"
      aria-label={`Show ${workspace.name}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "1vw",
        padding: "0 1.5vw",
        minHeight: "9vh",
        borderRadius: "18px",
        cursor: "pointer",
        position: "relative",
        background: isFocused ? surfaceHover : surface,
        border: "1px solid",
        borderColor: isCurrent ? "transparent" : ((dark && "rgba(255,255,255,0.08)") || "rgba(11,19,32,0.08)"),
        boxShadow: isCurrent
          ? `0 0 0 2px ${accent}, 0 12px 30px rgba(0,0,0,0.28)`
          : ((isFocused && `0 0 0 2px ${accent}88`) || "none"),
        transform: isFocused ? "translateY(-2px)" : "none",
        transition: "transform 160ms cubic-bezier(0.3,0.8,0.3,1), background 160ms ease, box-shadow 160ms ease",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {workspace._isSub && (
          <Box
            component="span"
            sx={{
              display: "block",
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: "1vh",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: faint,
              mb: "0.3vh",
            }}
          >
            {workspace._parentName} ›
          </Box>
        )}
        <Box
          sx={{
            fontFamily: FONT,
            fontWeight: 500,
            fontSize: "2.3vh",
            letterSpacing: "-0.01em",
            color: isCurrent ? "#FFFFFF" : text,
            lineHeight: 1.2,
          }}
        >
          {workspace.name}
        </Box>
      </Box>

      {isCurrent && (
        <Box
          sx={{
            width: "2.8vh",
            height: "2.8vh",
            flexShrink: 0,
            borderRadius: "50%",
            background: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <svg width="1.5vh" height="1.5vh" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: "1.5vh", height: "1.5vh" }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </Box>
      )}
    </Box>
  );
}

export default function TVWorkspaceSwitcher({
  open,
  onClose,
  workspaces = [],
  activeWorkspaceId,
  accent = "#5684d6",
  dark = true,
  railLeftVh = 1.4, // where the panel floats from the left edge
}) {
  const dispatch = useDispatch();
  const flat = useMemo(() => flattenWorkspaces(workspaces), [workspaces]);
  const listRef = useRef(null);

  const currentIdx = useMemo(() => {
    const i = flat.findIndex((w) => w._id === activeWorkspaceId);
    return i >= 0 ? i : 0;
  }, [flat, activeWorkspaceId]);

  // The keyboard/remote highlight — starts on the current workspace.
  const [focusIdx, setFocusIdx] = useState(currentIdx);
  useEffect(() => {
    if (open) setFocusIdx(currentIdx);
  }, [open, currentIdx]);

  const commit = useCallback(
    (idx) => {
      const ws = flat[idx];
      if (!ws) return;
      const { _parentName, _isSub, ...real } = ws;
      dispatch(setSelectedWorkspace({ selectedWorkspace: real }));
      onClose?.();
    },
    [flat, dispatch, onClose],
  );

  // Keep the focused card scrolled into view for long team lists.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${focusIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusIdx, open]);

  // D-pad / keyboard — only while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(flat.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        commit(focusIdx);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, focusIdx, flat.length, commit, onClose]);

  if (!open || !flat.length) return null;

  // Frosted, faintly-tinted glass — more transparent so the board glows through
  // for a futuristic feel; a hair of accent up top.
  const glass = dark
    ? "linear-gradient(158deg, rgba(38,39,42,0.86) 0%, rgba(28,29,32,0.9) 58%, rgba(20,21,24,0.92) 100%)"
    : "linear-gradient(158deg, rgba(255,255,255,0.92) 0%, rgba(246,248,252,0.84) 100%)";
  const border = dark ? "rgba(255,255,255,0.14)" : "rgba(11,19,32,0.10)";
  const scrollThumb = dark ? "rgba(255,255,255,0.16)" : "rgba(11,19,32,0.18)";
  const text = dark ? "#F4F7FC" : "#0B1320";
  const muted = dark ? "rgba(244,247,252,0.62)" : "rgba(11,19,32,0.6)";
  const faint = dark ? "rgba(244,247,252,0.36)" : "rgba(11,19,32,0.42)";

  return (
    <>
      {/* Scrim — dims the board; click to close. Sits above the board but below
          the rail (which stays crisp) and this panel. */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1990,
          background: dark ? "rgba(2,4,8,0.34)" : "rgba(20,30,50,0.24)",
          animation: "tvWsScrim 240ms ease",
        }}
      />
      <style>{`
        @keyframes tvWsScrim { from { opacity: 0; } to { opacity: 1; } }
        /* Slide the whole panel in from off the left edge — not a fade. */
        @keyframes tvWsPanel { from { transform: translateX(calc(-100% - 3vh)); } to { transform: translateX(0); } }
      `}</style>

      {/* Floating rounded glass panel */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          left: `${railLeftVh}vh`,
          top: "1.8vh",
          bottom: "1.8vh",
          width: "clamp(360px, 30vw, 500px)",
          zIndex: 2000,
          borderRadius: "28px",
          overflow: "hidden",
          background: glass,
          backdropFilter: "blur(42px) saturate(1.7)",
          WebkitBackdropFilter: "blur(42px) saturate(1.7)",
          border: `1px solid ${border}`,
          boxShadow: dark
            ? "0 34px 90px rgba(0,0,0,0.62), 0 0 0 1px rgba(120,160,255,0.06), inset 0 1px 0 rgba(255,255,255,0.14)"
            : "0 26px 64px rgba(20,40,80,0.18), inset 0 1px 0 rgba(255,255,255,0.7)",
          display: "flex",
          flexDirection: "column",
          transformOrigin: "left center",
          animation: "tvWsPanel 360ms cubic-bezier(0.32,0.9,0.3,1)",
          // Futuristic sheen — a soft accent glow bleeding down from the top edge.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            background: dark
              ? "radial-gradient(130% 55% at 50% -12%, rgba(86,132,214,0.12) 0%, transparent 62%)"
              : "none",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ padding: "2.6vh 1.5vw 0.6vh" }}>
          <Box sx={{ fontFamily: MONO_FONT, fontWeight: 500, fontSize: "1.1vh", letterSpacing: "0.16em", textTransform: "uppercase", color: faint }}>
            On this TV
          </Box>
          <Box sx={{ fontFamily: FONT, fontWeight: 500, fontSize: "2.4vh", letterSpacing: "-0.02em", color: text, mt: "0.6vh" }}>
            Choose a team
          </Box>
          <Box sx={{ fontFamily: FONT, fontSize: "1.35vh", color: muted, mt: "0.5vh", lineHeight: 1.45 }}>
            Each team plays the board set for it in TV&nbsp;View settings.
          </Box>
        </Box>

        {/* Team cards */}
        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "1.6vh 1.1vw 1vh",
            display: "flex",
            flexDirection: "column",
            gap: "1.1vh",
            position: "relative",
            zIndex: 1,
            // Thin, faint scrollbar — never the browser's default white bar.
            scrollbarWidth: "thin",
            scrollbarColor: `${scrollThumb} transparent`,
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: scrollThumb, borderRadius: "999px" },
          }}
        >
          {flat.map((w, i) => (
            <Box key={w._id || i} data-idx={i} sx={{ scrollMargin: "1vh" }}>
              <WorkspaceCard
                workspace={w}
                accent={accent}
                dark={dark}
                isCurrent={i === currentIdx}
                isFocused={i === focusIdx}
                onClick={() => { setFocusIdx(i); commit(i); }}
              />
            </Box>
          ))}
        </Box>

        {/* Footer hint — remote guidance */}
        <Box
          sx={{
            padding: "1.2vh 1vw 1.6vh",
            borderTop: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(11,19,32,0.08)"}`,
            display: "flex",
            justifyContent: "space-between",
            gap: "0.5vw",
            alignItems: "center",
            fontFamily: FONT,
            fontSize: "0.95vh",
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: faint,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}><KeyCap dark={dark}>↑</KeyCap><KeyCap dark={dark}>↓</KeyCap> Move</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}><KeyCap dark={dark} wide>OK</KeyCap> Show</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}><KeyCap dark={dark} wide>Back</KeyCap> Close</Box>
        </Box>
      </Box>
    </>
  );
}

function KeyCap({ children, wide, dark }) {
  return (
    <Box
      sx={{
        minWidth: wide ? "auto" : "2vh",
        height: "2vh",
        padding: wide ? "0 0.5vw" : 0,
        borderRadius: "5px",
        border: `1px solid ${dark ? "rgba(255,255,255,0.20)" : "rgba(11,19,32,0.18)"}`,
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(11,19,32,0.04)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT,
        fontSize: "1vh",
        fontWeight: 500,
        color: dark ? "rgba(255,255,255,0.9)" : "rgba(11,19,32,0.75)",
        textTransform: "none",
      }}
    >
      {children}
    </Box>
  );
}
